<template>
    <div class="history__container mb-3">
        <div class="row mt-2 history__table" v-for="h in historyData" :key="h.id">
            <div class="col-12">
                <div class="row">
                    <div class="col-8">
                        <p>
                            {{ getFullDateTime(h.bet_date) }} / {{ getTranslatedText(`transaction id`) }}:
                            &nbsp;
                            {{ h.transaction_id }}
                        </p>
                    </div>
                    <div class="col-4 d-flex justify-content-end">
                        <p>{{ getTranslatedText(`status`) }}:<span class="text" :class="{
                            'text__in-progress': h.bet_status === 'in_progress',
                            'text__settled': h.bet_status === 'settled',
                            'text__win': h.bet_status === 'win'
                        }">
                                {{ getTranslatedText(h.bet_status) }}</span>
                        </p>
                    </div>
                </div>
            </div>
            <div class="row m-0">
                <div class="history__table--info mt-2">
                    <div class="d-flex justify-content-between history__table--header" @click="handleExpand(h)">
                        <p class="item__value d-flex">
                            {{ !isMultiple(h) ? `${getHoursAndMinutes(h.bet_details[0].race_time)} -
                            ${h.bet_details[0].race_name}` : `${getTranslatedText("multiple")}
                            (${h.bet_details.length})` }}
                        </p>
                        <i :class="h.bet_id !== expandHistoryId ?
                            'bi bi-arrow-down-circle' : 'bi bi-arrow-up-circle'">
                        </i>
                    </div>

                    <div v-if="expandHistoryId == h.bet_id">
                        <div class="history__table--content" v-for="(hd, index) in historyDetails" :key="index">
                            <div class="row">
                                <p class="item__value d-flex" v-if="isMultiple(h)">{{
                                    getFullDateTime(hd.race_time)
                                    }} -
                                    {{ hd.race_name }}
                                </p>
                            </div>
                            <div class="row">
                                <div class="col-6">
                                    <p>{{ getTranslatedText(handleRenderMarketTypeName(hd)) }}</p>
                                </div>
                                <div class="col-6">
                                    <p class="item__title d-flex justify-content-end">
                                        <span class="text" :class="{
                                            'text__in-progress': hd.result_status === 'in_progress',
                                            'text__settled': hd.result_status === 'settled',
                                            'text__win': hd.result_status === 'win'
                                        }">
                                            {{ getTranslatedText(hd.result_status) }}</span>
                                    </p>
                                </div>
                            </div>
                            <div class="row mt-2" v-for="(selection, idx) in hd.selections" :key="idx">
                                <div class="col-6">
                                    <div class="row">
                                        <p class="d-flex">
                                            {{ selection.number + "-" + selection.race_field_name }}
                                        </p>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="row">
                                        <p class="item__title">
                                            {{ selection.odds ? getTranslatedText(`odds`) :
                                                getTranslatedText(`position`)
                                            }}:
                                            {{ selection.odds ? selection.odds : selection.position }}</p>
                                    </div>
                                </div>
                            </div>
                            <div class="row mt-2" v-if="index != h.bet_details.length - 1">
                                <hr />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-12 mt-2">
                <div class="betting-info">
                    <!-- Amount -->
                    <div class="betting-item">
                        <span class="label">{{ getTranslatedText(`amount`) }}:</span>
                        <span class="value">{{ currencySymbol }}&nbsp;{{ (Number(h.wager) + Number(h.tax)).toFixed(2) }}</span>
                    </div>
                    <!-- Wager -->
                    <div class="betting-item">
                        <span class="label">{{ getTranslatedText(`wagger`) }}:</span>
                        <span class="value">{{ currencySymbol }}&nbsp;{{ h.wager }}</span>
                    </div>
                    <!-- Tax -->
                    <div class="betting-item">
                        <span class="label">{{ getTranslatedText(`tax`) }}:</span>
                        <span class="value">
                            <span v-if="h.winning_tax_amount">{{ currencySymbol }}&nbsp;</span>{{ h.winning_tax_amount ? h.winning_tax_amount : "N/A" }}
                        </span>
                    </div>
                    <!-- Potential Payout -->
                    <div class="betting-item">
                        <span class="label">{{ getTranslatedText(`potential payout`) }}:</span>
                        <span class="value">
                            <span v-if="h.potential_winnings">{{ currencySymbol }}&nbsp;</span>{{ h.potential_winnings ? h.potential_winnings : "N/A" }}
                        </span>
                    </div>
                    <!-- Actual Payout -->
                    <div class="betting-item">
                        <span class="label">{{ getTranslatedText(`actual payout`) }}:</span>
                        <span class="value">
                            <span v-if="h.actual_payout">{{ currencySymbol }}&nbsp;</span>{{ h.actual_payout }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import languageMixins from "@/mixins/language/language-mixins";
import utilMixins from "@/mixins/utils/utils-mixins";

export default {
    name: 'HistoryTable',
    mixins: [languageMixins, utilMixins],
    props: {
        historyData: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            expandHistoryId: null,
            historyDetails: [],
        }
    },
    computed: {
        currencySymbol() {
            return this.$store.state.settings.currencySymbol;
        },
        isTax() {
            return this.$store.getters.isTax;
        }
    },
    methods: {
        isMultiple(item) {
            return item.bet_type_slug === "multi";
        },

        handleRenderMarketTypeName(market) {
            if (market.market_type == "win_or_ew" && market.selections[0].each_way_bet) return "Each way"
            if (market.market_type == "win_or_ew" && !market.selections[0].each_way_bet) return "Win"
            return market.market_type_name
        },

        handleExpand(h) {
            this.expandHistoryId = this.expandHistoryId === h.bet_id ? null : h.bet_id;
            this.historyDetails = this.expandHistoryId ? h.bet_details : [];
        },
    }
}
</script>

<style scoped>
.betting-info {
    display: grid;
    gap: 8px;
}

.betting-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
}

.betting-item .label {
    font-weight: 500;
    color: var(--color-text, #333);
}

.betting-item .value {
    font-weight: bold;
    color: var(--color-text, #000);
}

/* Desktop layout */
@media (min-width: 768px) {
    .betting-info {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: 16px;
    }
    
    .betting-item {
        flex-direction: column;
        align-items: flex-start;
        text-align: left;
        padding: 0;
    }
    
    .betting-item .label {
        margin-bottom: 4px;
        font-size: 14px;
    }
    
    .betting-item .value {
        font-size: 16px;
    }
}

/* Mobile layout */
@media (max-width: 767px) {
    .betting-info {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }
    
    .betting-item {
        justify-content: space-between;
        padding: 5px 0;
    }
    
    .betting-item:last-child {
        border-bottom: none;
    }
    
    .betting-item .label {
        font-size: 14px;
    }
    
    .betting-item .value {
        font-size: 14px;
    }
}
</style>